import React from 'react';
import { OrganizationIntegration } from '../../../types/integrations';
import { formatTimestampWithTimezone } from '../../../utils/timeHelpers';

interface IntegrationsTabProps {
  integrations: OrganizationIntegration[];
  loading: boolean;
  error: string;
  integrationLoading: boolean;
  onAddIntegration: () => void;
  onRevokeIntegration: (integrationId: string) => void;
  onRecreateIntegration: (integrationId: string) => void;
  onCopyLink: (url: string) => void;
  onViewWebhookData: (data: any) => void;
  onNavigateToIntegration: (integrationId: string) => void;
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({
  integrations,
  loading,
  error,
  integrationLoading,
  onAddIntegration,
  onRevokeIntegration,
  onRecreateIntegration,
  onCopyLink,
  onViewWebhookData,
  onNavigateToIntegration
}) => {
  // Helper function to get latest webhook event info
  const getLatestWebhookEvent = (integration: OrganizationIntegration) => {
    if (!integration.integration_data || !Array.isArray(integration.integration_data) || integration.integration_data.length === 0) {
      return { eventType: 'No events', status: 'N/A', timestamp: 'N/A' };
    }

    const latestEvent = integration.integration_data[integration.integration_data.length - 1];

    // Determine event type and status based on the event structure
    let eventType = 'Unknown';
    let status = 'Unknown';
    let timestamp = 'N/A';

    // Handle direct Xero integration callback data
    if (integration.integration_provider === 'xero_direct' && latestEvent.authorization_method === 'direct_oauth') {
      eventType = 'Xero Authorization';
      status = 'COMPLETE';
      timestamp = latestEvent.authorization_timestamp || 'N/A';

      // Add tenant information if available
      if (latestEvent.tenants && latestEvent.tenants.length > 0) {
        const tenantCount = latestEvent.tenants.length;
        const defaultTenant = latestEvent.tenants.find((t: any) => t.is_default);
        if (defaultTenant) {
          eventType = `Xero Authorization (${tenantCount} tenant${tenantCount > 1 ? 's' : ''})`;
        }
      }
    } else if (latestEvent.integration_slug) {
      // This is a linked_account event (Merge.dev)
      eventType = 'Account Linked';
      status = latestEvent.status || 'Unknown';
      timestamp = latestEvent.completed_at || 'N/A';
    } else if (latestEvent.sync_status) {
      // This is a sync_completed event (Merge.dev)
      eventType = 'Sync Completed';
      status = latestEvent.sync_status || 'Unknown';
      timestamp = latestEvent.last_sync_completed_at || 'N/A';
    } else if (latestEvent.request_body?.hook?.event) {
      // Fallback to webhook event type
      eventType = latestEvent.request_body.hook.event;
      status = 'Processed';
      timestamp = latestEvent.request_body.hook.id ? 'N/A' : 'N/A';
    }

    return { eventType, status, timestamp };
  };

  return (
    <div className="px-6 pb-6">
      <div className="space-y-4 pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Organization Integrations</h3>
          <button
            onClick={onAddIntegration}
            disabled={integrationLoading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            {integrationLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7l9-11h-7z" />
              </svg>
            )}
            <span>{integrationLoading ? 'Creating...' : 'Add Integration'}</span>
          </button>
        </div>

        {error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Integration</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Latest Webhook Event</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                          Loading integrations...
                        </div>
                      </td>
                    </tr>
                  ) : integrations.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                        No integrations found for this organization
                      </td>
                    </tr>
                  ) : (
                    integrations.map((integration) => {
                      const latestEvent = getLatestWebhookEvent(integration);
                      return (
                        <tr key={integration.id} className="hover:bg-gray-50">
                          <td className="px-3 py-4 text-left">
                            <div className="flex items-center">
                              {integration.integration?.icon_url ? (
                                <img
                                  src={integration.integration.icon_url}
                                  alt={`${integration.integration.display_name} icon`}
                                  className="w-10 h-10 rounded-lg object-contain"
                                  onError={(e) => {
                                    // Fallback to text if image fails to load
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    target.nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                              ) : null}

                              {/* Fallback Icon */}
                              <div className={`w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm ${integration.integration?.icon_url ? 'hidden' : ''
                                }`}>
                                {integration.integration?.display_name?.charAt(0) || 'I'}
                              </div>

                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {integration.integration_provider === 'xero_direct' ? (
                                    <button
                                      onClick={() => onNavigateToIntegration(integration.id)}
                                      className="text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                                    >
                                      {integration.integration?.display_name || 'Unknown Integration'}
                                    </button>
                                  ) : (
                                    integration.integration?.display_name || 'Unknown Integration'
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-left">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${integration.authorization_status === 'authorized' ? 'bg-green-100 text-green-800' :
                                integration.authorization_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  integration.authorization_status === 'failed' ? 'bg-red-100 text-red-800' :
                                    integration.authorization_status === 'expired' ? 'bg-orange-100 text-orange-800' :
                                      integration.authorization_status === 'revoked' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                              }`}>
                              {integration.authorization_status}
                            </span>
                            {integration.expired_at && (
                              <div className="text-xs text-gray-500 mt-1">
                                Expires: {formatTimestampWithTimezone(integration.expired_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-4 text-left">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm text-gray-900">
                                <div className="font-medium">{latestEvent.eventType}</div>
                                <div className="text-xs text-gray-500">
                                  Status: {latestEvent.status}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatTimestampWithTimezone(latestEvent.timestamp)}
                                </div>
                              </div>
                              {integration.integration_data && Array.isArray(integration.integration_data) && integration.integration_data.length > 0 && (
                                <button
                                  onClick={() => onViewWebhookData(integration.integration_data)}
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                                  title="View webhook data"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-4 text-left text-sm text-gray-900">
                            {formatTimestampWithTimezone(integration.created_at)}
                          </td>
                          <td className="px-3 py-4 text-left text-sm font-medium">
                            <div className="flex space-x-2">
                              {integration.authorization_status === 'authorized' && (
                                <>
                                  <button
                                    onClick={() => onRevokeIntegration(integration.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Revoke
                                  </button>
                                </>
                              )}
                              {integration.authorization_status === 'pending' && integration.link_to_authorize && (
                                <button
                                  onClick={() => onCopyLink(integration.link_to_authorize!)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Copy Link
                                </button>
                              )}
                              {integration.authorization_status === 'expired' && (
                                <button
                                  onClick={() => onRecreateIntegration(integration.id)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  Re-create
                                </button>
                              )}
                              {integration.authorization_status === 'revoked' && (
                                <button
                                  onClick={() => onRecreateIntegration(integration.id)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  Re-create
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsTab;
